import { useMemo, type ReactNode } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle } from "lucide-react";
import { COR_DA_FAIXA, numeroBR, type FaixaAderencia } from "@shared/irrigacao";
import { dataCurtaBR } from "@shared/boletim-periodo";
import type { BarraDeSetor, DashboardIrrigacao, PontoDaSerie } from "@/hooks/use-irrigacao";

// Gráficos em SVG puro — o repo não tem biblioteca de gráfico para isto e acrescentar
// dependência exigiria regravar o package-lock, que o CI consome com `npm ci`.
//
// ⚠️ A ESCALA É DIVERGENTE: azul (água demais) ↔ laranja (seco), cinza dentro de ±10%.
// NÃO trocar por verde/vermelho — em protanopia esta paleta tem ΔE 24,7 e o semáforo
// tem 4,1, ilegível. E a cor nunca é canal único: todo gráfico traz o número escrito.

const CINZA_SEM_META = "#c8ccc4";

function corDaBarra(faixa: FaixaAderencia | null): string {
  return faixa ? COR_DA_FAIXA[faixa] : CINZA_SEM_META;
}

function Tile({ titulo, valor, detalhe, alerta }: {
  titulo: string;
  valor: ReactNode;
  detalhe?: ReactNode;
  alerta?: boolean;
}) {
  return (
    <div className={`rounded-md border p-3 ${alerta ? "border-atencao/30 bg-atencao-suave" : ""}`}>
      <p className="text-xs font-medium text-muted-foreground">{titulo}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums">{valor}</p>
      {detalhe && <p className="mt-0.5 text-xs text-muted-foreground">{detalhe}</p>}
    </div>
  );
}

function Tiles({ dados }: { dados: DashboardIrrigacao }) {
  const { cobertura } = dados;
  const pctCobertura = cobertura.diasDoPeriodo > 0 ? (cobertura.diasComLeitura / cobertura.diasDoPeriodo) * 100 : 0;

  // Aderência: média das unidades presentes, cada uma já ponderada no servidor. Delta
  // contra o período anterior de mesmo tamanho.
  const comAderencia = dados.porUnidade.filter(u => u.aderenciaMedia !== null);
  const aderenciaAtual = comAderencia.length
    ? comAderencia.reduce((a, u) => a + (u.aderenciaMedia ?? 0), 0) / comAderencia.length
    : null;
  const anteriorComAderencia = dados.anterior.porUnidade.filter(u => u.aderenciaMedia !== null);
  const aderenciaAntes = anteriorComAderencia.length
    ? anteriorComAderencia.reduce((a, u) => a + (u.aderenciaMedia ?? 0), 0) / anteriorComAderencia.length
    : null;
  const delta = aderenciaAtual !== null && aderenciaAntes !== null ? (aderenciaAtual - aderenciaAntes) * 100 : null;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Tile
        titulo="Lâmina média aplicada"
        valor={
          dados.porUnidade.length === 0 ? "—" : (
            // Nunca um número só quando o filtro mistura culturas: mm e L/planta não somam.
            <span className="flex flex-wrap items-baseline gap-x-3">
              {dados.porUnidade.map(u => (
                <span key={u.unidade}>
                  {numeroBR(u.laminaMediaDiaria, 2)}
                  <span className="ml-1 text-xs font-normal text-muted-foreground">{u.unidadeRotulo}/dia</span>
                </span>
              ))}
            </span>
          )
        }
        detalhe={dados.porUnidade.map(u => `${u.setores} setores`).join(" · ")}
      />
      <Tile
        titulo="Aderência à meta"
        valor={aderenciaAtual === null ? "sem meta" : `${numeroBR(aderenciaAtual * 100, 0)}%`}
        detalhe={
          delta === null
            ? "sem comparação com o período anterior"
            : `${delta >= 0 ? "+" : ""}${numeroBR(delta, 0)} p.p. vs. ${dataCurtaBR(dados.anterior.de)}–${dataCurtaBR(dados.anterior.ate)}`
        }
      />
      <Tile
        titulo="Água aplicada"
        valor={<span>{numeroBR(dados.aguaAplicadaM3, 0)}<span className="ml-1 text-xs font-normal text-muted-foreground">m³</span></span>}
        detalhe={`${numeroBR(dados.anterior.aguaAplicadaM3, 0)} m³ no período anterior`}
      />
      <Tile
        titulo="Cobertura"
        valor={`${cobertura.diasComLeitura} de ${cobertura.diasDoPeriodo} dias`}
        detalhe={
          <>
            {numeroBR(pctCobertura, 0)}% do período
            {cobertura.diasRecusados > 0 && ` · ${cobertura.diasRecusados} recusado(s)`}
            {cobertura.diasEmManutencao > 0 && ` · ${cobertura.diasEmManutencao} em manutenção`}
          </>
        }
        alerta={pctCobertura < 100}
      />
    </div>
  );
}

function BarrasPorSetor({ barras }: { barras: BarraDeSetor[] }) {
  const maximo = useMemo(() => {
    let m = 0;
    for (let i = 0; i < barras.length; i++) {
      m = Math.max(m, barras[i].aplicado, barras[i].meta ?? 0);
    }
    return m > 0 ? m * 1.08 : 1;
  }, [barras]);

  if (barras.length === 0) {
    return <p className="py-10 text-center text-sm text-muted-foreground">Nenhum registro no período.</p>;
  }

  return (
    <div className="space-y-1.5">
      {barras.map(b => {
        const largura = Math.min(100, (b.aplicado / maximo) * 100);
        const tickMeta = b.meta !== null ? Math.min(100, (b.meta / maximo) * 100) : null;
        const desvio = b.aderencia !== null ? Math.round((b.aderencia - 1) * 100) : null;
        return (
          <div key={b.setorId} className="flex items-center gap-2 text-sm">
            <div className="w-24 shrink-0 truncate">
              <span className="font-medium">{b.nome}</span>
              {b.metaParcial && <span className="ml-1 text-[10px] text-atencao">meta parcial</span>}
            </div>
            <div className="relative h-5 flex-1 rounded bg-muted">
              <div className="h-full rounded" style={{ width: `${largura}%`, background: corDaBarra(b.faixa) }} />
              {tickMeta !== null && (
                <div
                  className="absolute top-[-2px] h-[calc(100%+4px)] w-[2px] bg-foreground/70"
                  style={{ left: `${tickMeta}%` }}
                  title={`Meta ${numeroBR(b.meta, 1)} ${b.unidadeRotulo}`}
                />
              )}
            </div>
            {/* A cor nunca é canal único — o número vai escrito, sempre. */}
            <div className="w-32 shrink-0 text-right tabular-nums">
              {numeroBR(b.aplicado, 1)} <span className="text-xs text-muted-foreground">{b.unidadeRotulo}</span>
              {desvio !== null ? (
                <span className="ml-1.5 text-xs" style={{ color: corDaBarra(b.faixa) }}>
                  {desvio > 0 ? "+" : ""}{desvio}%
                </span>
              ) : (
                <span className="ml-1.5 text-xs text-muted-foreground">sem meta</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EvolucaoDiaria({ serie, unidade, rotulo }: { serie: PontoDaSerie[]; unidade: "mm" | "L_PLANTA"; rotulo: string }) {
  // ⚠️ sem spread em chamada — o typecheck do CI compila em alvo antigo, e reduce é
  // igualmente claro.
  const maximo = serie.reduce((m, p) => Math.max(m, p.porUnidade[unidade] ?? 0), 1) * 1.15;
  const largura = Math.max(320, serie.length * 26);
  const altura = 140;
  const larguraColuna = largura / Math.max(1, serie.length);

  return (
    <div className="overflow-x-auto">
      <svg width={largura} height={altura + 26} role="img" aria-label={`Lâmina diária em ${rotulo}`}>
        <defs>
          {/* Dia SEM LEITURA é hachurado, não zerado: o buraco tem que aparecer como buraco. */}
          <pattern id="semLeitura" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill="#eef0ec" />
            <line x1="0" y1="0" x2="0" y2="6" stroke="#c8ccc4" strokeWidth="2.5" />
          </pattern>
          {/* Dia DIVERGENTE entra, mas marcado — nunca conta como dado limpo. */}
          <pattern id="divergente" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="5" height="5" fill={COR_DA_FAIXA.na_meta} />
            <line x1="0" y1="0" x2="0" y2="5" stroke="#ffffff" strokeWidth="1.6" />
          </pattern>
        </defs>
        {serie.map((p, i) => {
          const x = i * larguraColuna + 2;
          const w = Math.max(4, larguraColuna - 4);
          const valor = p.porUnidade[unidade];
          if (p.semLeitura || valor === null) {
            return (
              <g key={p.data}>
                <rect x={x} y={8} width={w} height={altura - 8} fill="url(#semLeitura)" rx={2} />
                <title>{`${dataCurtaBR(p.data)} — ${p.manutencao ? "manutenção" : p.recusado ? "documento recusado" : "sem leitura"}`}</title>
              </g>
            );
          }
          const h = Math.max(2, (valor / maximo) * (altura - 8));
          return (
            <g key={p.data}>
              <rect
                x={x} y={altura - h} width={w} height={h} rx={2}
                fill={p.divergente ? "url(#divergente)" : COR_DA_FAIXA.na_meta}
              />
              <title>{`${dataCurtaBR(p.data)} — ${numeroBR(valor, 2)} ${rotulo}${p.divergente ? " (divergente)" : ""}`}</title>
            </g>
          );
        })}
        {serie.map((p, i) => (
          i % Math.ceil(serie.length / 12) === 0 ? (
            <text
              key={`r-${p.data}`} x={i * larguraColuna + larguraColuna / 2} y={altura + 16}
              textAnchor="middle" fontSize="9" fill="currentColor" opacity={0.6}
            >
              {dataCurtaBR(p.data)}
            </text>
          ) : null
        ))}
      </svg>
    </div>
  );
}

function Heatmap({ dados }: { dados: DashboardIrrigacao }) {
  const datas = dados.serie.map(p => p.data);
  const setores = dados.barras.map(b => ({ id: b.setorId, nome: b.nome, rotulo: b.unidadeRotulo }));
  const porCelula = useMemo(() => {
    const mapa = new Map<string, { valor: number; faixa: FaixaAderencia | null }>();
    for (let i = 0; i < dados.heatmap.length; i++) {
      const c = dados.heatmap[i];
      mapa.set(`${c.setorId}:${c.data}`, { valor: c.valor, faixa: c.faixa });
    }
    return mapa;
  }, [dados.heatmap]);

  if (setores.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <table className="text-[10px]">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-background px-2 py-1 text-left" />
            {datas.map(d => (
              <th key={d} className="px-0.5 py-1 font-normal text-muted-foreground">{dataCurtaBR(d).slice(0, 2)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {setores.map(s => (
            <tr key={s.id}>
              <th className="sticky left-0 z-10 bg-background px-2 py-0.5 text-left text-xs font-medium">{s.nome}</th>
              {datas.map(d => {
                const celula = porCelula.get(`${s.id}:${d}`);
                if (!celula) {
                  return <td key={d} className="px-0.5 py-0.5"><div className="h-5 w-7 rounded-sm bg-muted" title={`${s.nome} · ${dataCurtaBR(d)} — sem leitura`} /></td>;
                }
                return (
                  <td key={d} className="px-0.5 py-0.5">
                    <div
                      className="flex h-5 w-7 items-center justify-center rounded-sm text-[9px] font-semibold text-white"
                      style={{ background: corDaBarra(celula.faixa) }}
                      title={`${s.nome} · ${dataCurtaBR(d)} — ${numeroBR(celula.valor, 2)} ${s.rotulo}`}
                    >
                      {numeroBR(celula.valor, 1)}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Legenda() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <span className="h-3 w-3 rounded-sm" style={{ background: COR_DA_FAIXA.excesso }} /> acima da meta
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-3 w-3 rounded-sm" style={{ background: COR_DA_FAIXA.na_meta }} /> na faixa (±10%)
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-3 w-3 rounded-sm" style={{ background: COR_DA_FAIXA.deficit }} /> abaixo da meta
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-3 w-3 rounded-sm" style={{ background: CINZA_SEM_META }} /> sem meta
      </span>
    </div>
  );
}

export function DashboardIrrigacaoPainel({ consulta }: {
  consulta: { data?: DashboardIrrigacao; isLoading: boolean; isError: boolean; error: unknown };
}) {
  if (consulta.isLoading) {
    return <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}</div>;
  }
  if (consulta.isError || !consulta.data) {
    return (
      <p className="py-16 text-center text-sm text-erro">
        {(consulta.error as any)?.message ?? "Erro ao carregar o dashboard."}
      </p>
    );
  }
  const dados = consulta.data;

  return (
    <div className="space-y-4">
      <Tiles dados={dados} />

      {dados.diasDivergentes.length > 0 && (
        <div className="flex items-start gap-1.5 rounded-md border border-atencao/30 bg-atencao-suave px-3 py-2 text-xs text-atencao">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {dados.diasDivergentes.length} {dados.diasDivergentes.length === 1 ? "dia entrou marcado" : "dias entraram marcados"} como
            divergente e {dados.diasDivergentes.length === 1 ? "aparece hachurado" : "aparecem hachurados"} nos gráficos
            ({dados.diasDivergentes.map(d => dataCurtaBR(d)).join(", ")}). Os números do período incluem esses dias.
          </span>
        </div>
      )}

      <div className="rounded-md border">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <div>
            <h2 className="font-semibold">Lâmina por setor</h2>
            <p className="text-sm text-muted-foreground">Acumulada no período, com a marca da meta acumulada.</p>
          </div>
          <Legenda />
        </div>
        <div className="p-4"><BarrasPorSetor barras={dados.barras} /></div>
      </div>

      {dados.porUnidade.map(u => (
        <div key={u.unidade} className="rounded-md border">
          <div className="border-b px-4 py-3">
            <h2 className="font-semibold">Evolução diária — {u.unidadeRotulo}</h2>
            <p className="text-sm text-muted-foreground">
              Dia hachurado em cinza é dia <b>sem leitura</b> — não é zero nem média. Coluna listrada é dia divergente.
            </p>
          </div>
          <div className="p-4">
            <EvolucaoDiaria serie={dados.serie} unidade={u.unidade} rotulo={u.unidadeRotulo} />
          </div>
        </div>
      ))}

      <div className="rounded-md border">
        <div className="border-b px-4 py-3">
          <h2 className="font-semibold">Aderência setor × dia</h2>
          <p className="text-sm text-muted-foreground">Onde procurar o setor que vive fora da faixa.</p>
        </div>
        <div className="p-4"><Heatmap dados={dados} /></div>
      </div>

      {dados.cobertura.diasEmManutencao > 0 && (
        <p className="text-xs text-muted-foreground">
          <Badge variant="secondary" className="mr-1.5">manutenção</Badge>
          {dados.cobertura.diasEmManutencao} dia(s) marcado(s) como manutenção ficaram <b>fora</b> de todas as séries —
          teste de vazão e reparo não são irrigação.
        </p>
      )}
    </div>
  );
}
